<template>
  <div class="chat-box">
    <!-- Messages -->
    <div class="messages" ref="messages">
      <div v-for="(msg, i) in messages" :key="i">
        <div :class="['message-row', msg.role]">
          <div class="message-bubble">
            <template v-if="msg.role === 'assistant' && !msg.content && !msg.link">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </template>
            <template v-else>
              <span>{{ msg.content }}</span>
              <a
                v-if="msg.link && msg.link.url && msg.link.label"
                :href="msg.link.url"
                class="chat-link d-block mt-1"
              >{{ msg.link.label }}</a>
            </template>
          </div>
        </div>
        <div v-if="msg.showChips" class="inline-suggestions px-2 pb-2">
          <CButton
            v-for="(s, si) in suggestions"
            :key="si"
            size="sm"
            color="light"
            class="mr-2 mb-2"
            @click="selectSuggestion(s)"
          >{{ s }}</CButton>
        </div>
      </div>

      <!-- <div v-if="loading" class="typing-container">
        <span class="dot"></span>
        <span class="dot"></span>
        <span class="dot"></span>
      </div> -->
    </div>

    <!-- Input -->
    <div class="p-2 border-top">
      <div v-if="showSuggestions" class="suggestions px-2 pb-2">
        <CButton
          v-for="(s, i) in suggestions"
          :key="i"
          size="sm"
          color="light"
          class="mr-2 mb-2"
          @click="selectSuggestion(s)"
        >
          {{ s }}
        </CButton>
      </div>
      <div class="input-box d-flex justify-content-between align-items-center">
        <TextareaInput
          ref="chatInput"
          name="chatInput"
          :value="input"
          @input="handleInput"
          placeholder="Ask something..."
          @enterEvent="sendMessage"
          class="w-100"
        />

        <CButton
          color="primary"
          variant="ghost"
          class="ml-2 d-flex align-items-center justify-content-center"
          @click="sendMessage"
          :disabled="loading"
          style="width: 40px; height: 40px"
          v-c-tooltip="'Send'"
        >
          <i class="fa fa-paper-plane" aria-hidden="true"></i>
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
import TextareaInput from "@/components/reusable/Fields/TextareaInput";
import { mapActions } from "vuex";

const ALL_SUGGESTIONS = [
  "How do I register?",
  "Find jobs",
  "Career pathway",
  "Contact support",
  "Home page",
];

const COMMON_SUGGESTIONS = [
  "How do I register?",
  "Find jobs",
  "Home page",
];

export default {
  data() {
    return {
      input: "",
      messages: [],
      loading: false,
    };
  },
  components: {
    TextareaInput,
  },
  computed: {
    showSuggestions() {
      return this.messages.length === 0;
    },
    suggestions() {
      const acronym = this.$route.path.split("/").filter(Boolean)[0];
      return acronym === "tfm" ? ALL_SUGGESTIONS : COMMON_SUGGESTIONS;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.chatInput.$el.querySelector("textarea").focus();
    });
  },
  methods: {
    ...mapActions(["sendChatMessage"]),
    selectSuggestion(text) {
      this.input = text;
      this.sendMessage();
    },
    async sendMessage() {
      if (!this.input.trim()) return;

      const text = this.input;

      this.messages.push({
        role: "user",
        content: text,
      });

      this.input = "";
      this.loading = true;
      const assistantMsg = { role: "assistant", content: "" };
      this.messages.push(assistantMsg);

      try {
        const reply = await this.sendChatMessage(text);
        const { text: replyText, link, showChips } = reply;

        if (link) {
          assistantMsg.content = replyText;
          this.$set(assistantMsg, "link", link);
        } else {
          for (let i = 0; i < replyText.length; i++) {
            assistantMsg.content += replyText[i];
            await new Promise((r) => setTimeout(r, 10));
          }
        }
        if (showChips) {
          this.$set(assistantMsg, "showChips", true);
        }
        this.scrollToBottom();
        if (!showChips) {
          setTimeout(() => {
            this.messages.push({
              role: "assistant",
              content: "Please type 'help' to see the available options!",
            });
            this.scrollToBottom();
          }, 1000);
        }
      } catch (e) {
        console.error(e);
        this.messages.push({
          role: "assistant",
          content: "Error fetching response",
        });
      }

      this.loading = false;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messages;
        el.scrollTop = el.scrollHeight;
      });
    },

    handleInput(name, value) {
      this.input = value;
    },
  },
};
</script>

<style scoped>
.chat-box {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.typing-container {
  display: flex;
  align-items: center;
  padding: 10px;
}

.dot {
  width: 6px;
  height: 6px;
  margin: 0 3px;
  background-color: #999;
  border-radius: 50%;
  animation: bounce 1.2s infinite ease-in-out;
}

/* delay each dot */
.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-link {
  display: inline-block;
  color: #4e73df;
  font-weight: 600;
  text-decoration: underline;
}

.message-row.user .chat-link {
  color: #cce0ff;
}

.suggestions {
  display: flex;
  flex-wrap: wrap;
}

.suggestions .btn {
  border-radius: 20px;
  font-size: 12px;
  text-transform: none;
}

.inline-suggestions {
  display: flex;
  flex-wrap: wrap;
}

.inline-suggestions .btn {
  border-radius: 20px;
  font-size: 12px;
  text-transform: none;
}

.message-row {
  display: flex;
  margin-bottom: 8px;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.assistant {
  justify-content: flex-start;
}

/* Bubble */
.message-bubble {
  max-width: 75%;
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.4;
}

/* User bubble */
.message-row.user .message-bubble {
  background: #4e73df;
  color: white;
  border-bottom-right-radius: 4px;
}

/* Assistant bubble */
.message-row.assistant .message-bubble {
  background: #f1f1f1;
  color: #333;
  border-bottom-left-radius: 4px;
}

.dot {
  width: 6px;
  height: 6px;
  margin: 0 2px;
  background-color: #999;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1.2s infinite ease-in-out;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}
.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
